/**
 * Chart of Accounts Page
 *
 * Displays the hierarchical account structure with CRUD operations.
 * Features lazy loading and hyphen-based depth indicators.
 */

import { useEffect, useState } from 'react'


import { usePageHeader, useShortcuts, useEscapeNavigation } from '@elsapiens/providers'
import { Plus } from 'lucide-react'
import { useNavigate , Link } from 'react-router-dom'


import { AccountActions } from './AccountActions'
import { SystemAccountsModal } from './SystemAccountsModal'
import { AccountFilters } from './AccountFilters'
import { AccountForm } from './AccountForm'
import { AccountFormWizard } from './AccountFormWizard'
import { AccountTree } from './AccountTree'
import { COA_STEPS, COA_WHAT_IS_IT, buildCoaFeatures } from './chart-of-accounts-helpers'
import { useChartOfAccounts } from './useChartOfAccounts'
import { ChartOfAccountsIllustration } from '../../components/illustrations/AccountingIllustrations'
import { CollapsibleListInfo, CursorRangeText, EmptyState } from '../../components/ui'

const COA_FEATURES = buildCoaFeatures()

type CoaState = ReturnType<typeof useChartOfAccounts>

function useCoaShortcuts(coa: CoaState): void {
  const { register, pushContext, popContext } = useShortcuts()
  useEffect(() => {
    pushContext('chart-of-accounts')
    return () => popContext('chart-of-accounts')
  }, [pushContext, popContext])
  useEffect(() => {
    return register({ id: 'coa-new', keys: 'n', label: 'New Account', description: 'Create a new account', category: 'Chart of Accounts', scope: 'context', context: 'chart-of-accounts', action: coa.handleOpenCreate })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register])
}

function CoaInfoBlock({ coa }: { coa: CoaState }): JSX.Element | null {
  if (coa.isLoading) { return null }
  if (coa.accountsMap.size === 0) {
    return (
      <EmptyState
        description="The chart of accounts defines every account your organization uses to classify transactions. Create your first account to get started."
        features={COA_FEATURES}
        gettingStarted={COA_STEPS}
        illustration={<ChartOfAccountsIllustration size="lg" />}
        primaryAction={{
          label: 'Create Account',
          icon: <Plus className="w-4 h-4 mr-2" />,
          onClick: coa.handleOpenCreate,
        }}
        title="No accounts yet"
        whatIsIt={COA_WHAT_IS_IT}
      />
    )
  }
  return (
    <CollapsibleListInfo
      description="Your chart of accounts organizes all financial accounts into a hierarchical structure for accurate bookkeeping."
      features={COA_FEATURES}
      gettingStarted={COA_STEPS}
      illustration={<ChartOfAccountsIllustration size="lg" />}
      title="About the Chart of Accounts"
      whatIsIt={COA_WHAT_IS_IT}
    />
  )
}

// AccountTree is a hierarchical tree (loaded top-down/in full), not a flat
// page — so it gets only the CursorRangeText status caption, never the full
// CursorRangePagination row with Prev/Next (see pagination design spec).
function CoaLoadedStatus({ coa }: { coa: CoaState }): JSX.Element | null {
  if (coa.isLoading || coa.loadedCount === 0) { return null }
  return (
    <div className="el-mt-sm">
      <CursorRangeText
        isExact={coa.remainingIsExact}
        rangeEnd={coa.loadedCount}
        rangeStart={1}
        total={coa.loadedCount + coa.remainingCount}
      />
    </div>
  )
}

function CoaFormSwitch({ coa }: { coa: CoaState }): JSX.Element {
  const sharedProps = {
    accountsMap: coa.accountsMap,
    baseCurrency: coa.baseCurrency,
    currencies: coa.enabledCurrencies,
    formData: coa.formData,
    isLoadingBankDetails: coa.isLoadingBankDetails,
    isOpen: coa.isModalOpen,
    isSaving: coa.isSaving,
    isShowCurrencyConfirm: coa.isShowCurrencyConfirm,
    parentOptions: coa.parentOptions,
    setFormData: coa.setFormData,
    setTagInput: coa.setTagInput,
    tagInput: coa.tagInput,
    onClose: () => coa.setIsModalOpen(false),
    onCurrencyConfirm: () => { void coa.handleSave() },
    onCurrencyConfirmClose: () => coa.setIsShowCurrencyConfirm(false),
    onSave: () => { void coa.handleSave() },
  }
  return coa.editingAccount
    ? <AccountForm {...sharedProps} editingAccount={coa.editingAccount} />
    : <AccountFormWizard {...sharedProps} />
}

function useCoaPageChrome(): void {
  const navigate = useNavigate()
  const coaBreadcrumbs = [
    { label: 'Home', href: '/', as: Link },
    { label: 'Accounting', href: '/accounting', as: Link },
    { label: 'Chart of Accounts' },
  ]
  usePageHeader({
    title: 'Chart of Accounts',
    description: "Manage your organization's account structure",
    breadcrumbs: coaBreadcrumbs,
  })
  useEscapeNavigation(navigate, coaBreadcrumbs)
}

export function ChartOfAccounts(): JSX.Element {
  const navigate = useNavigate()
  const coa = useChartOfAccounts()
  const [isSystemAccountsOpen, setIsSystemAccountsOpen] = useState(false)
  useCoaShortcuts(coa)
  useCoaPageChrome()

  return (
    <div className="flex flex-col min-w-0">
      <AccountActions
        onCreateAccount={coa.handleOpenCreate}
        onSystemAccounts={() => setIsSystemAccountsOpen(true)}
      />
      <SystemAccountsModal
        accountsMap={coa.accountsMap}
        isOpen={isSystemAccountsOpen}
        reservedAccounts={coa.reservedAccounts}
        onClose={() => setIsSystemAccountsOpen(false)}
        onCreateReserved={coa.handleOpenCreateReserved}
      />
      <div className="el-p-lg">
        <AccountFilters
          activeTab={coa.activeTab}
          currencyOptions={coa.currencyOptions}
          filterConditions={coa.filterConditions}
          searchQuery={coa.searchQuery}
          sortDirection={coa.sortDirection}
          sortField={coa.sortField}
          subtypeOptions={coa.subtypeOptions}
          onFilterConditionsChange={coa.setFilterConditions}
          onSearchChange={coa.setSearchQuery}
          onSortChange={(id, dir) => { coa.setSortField(id); coa.setSortDirection(dir) }}
          onTabChange={coa.setActiveTab}
        />
        <AccountTree
          accountsMap={coa.accountsMap}
          baseCurrency={coa.baseCurrency}
          childrenMap={coa.childrenMap}
          expandedTypes={coa.expandedTypes}
          isLoading={coa.isLoading}
          orgId={coa.organizationId}
          rowsByType={coa.rowsByType}
          onEdit={(account) => { void coa.handleOpenEdit(account) }}
          onToggleAccount={(id) => { void coa.toggleAccountExpanded(id) }}
          onToggleActive={(account) => { void coa.handleToggleActive(account) }}
          onToggleType={coa.toggleTypeExpanded}
          onViewLedger={(accountId) => navigate(`/reports/general-ledger?accountId=${accountId}`)}
        />
        <CoaLoadedStatus coa={coa} />
        <CoaInfoBlock coa={coa} />
        <CoaFormSwitch coa={coa} />
      </div>
    </div>
  )
}
